import type { RascunhoDivisao } from './cadastroAdmin'
import { marcasLinxDoTexto, nomesDasMarcasLinx } from './cadastroAdmin'

/**
 * Aba Marcas — uma linha por divisão. Porte de `prototipo/admin.html`.
 *
 * O componente é só a tela: quem guarda o rascunho e quem fala com a API é a
 * página. Assim o PUT das divisões e o POST das logos ficam numa sequência só,
 * em um lugar só.
 */

type Props = {
  rascunhos: RascunhoDivisao[]
  bases: string[]
  subitensDisponiveis: Array<{ codigo: string; rotulo: string }>
  aoAlterar: (chave: string, mudanca: Partial<RascunhoDivisao>) => void
  aoAlternarSubitem: (chave: string, codigo: string) => void
  aoEscolherLogo: (chave: string, arquivo: File | null) => void
  aoAdicionar: () => void
  aoDescartar: (chave: string) => void
  aoSalvar: () => void
  salvando: boolean
  salvo: boolean
  erro: string | null
}

export function AbaMarcas({
  rascunhos, bases, subitensDisponiveis,
  aoAlterar, aoAlternarSubitem, aoEscolherLogo, aoAdicionar, aoDescartar, aoSalvar,
  salvando, salvo, erro,
}: Props) {
  return (
    <section>
      <h2>Marcas do painel</h2>
      <p className="ajuda">
        Cada linha vira um card no dashboard. O <b>de-para do ERP</b> diz quais valores da
        coluna <code>Produto Marca</code> alimentam aquele card — é aqui que se resolve, sem
        mexer em código, uma marca que no cadastro aparece com mais de uma grafia. Mais de um
        valor na mesma linha, separados por ponto e vírgula.
      </p>

      <div className="painel-admin">
        <table>
          <thead>
            <tr>
              <th className="num estreito">Ordem</th>
              <th>Logo</th>
              <th>Nome no painel</th>
              <th className="coluna-base">Base</th>
              <th>De-para do ERP</th>
              <th>Subitens exibidos</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rascunhos.length === 0 ? (
              <tr>
                <td colSpan={7} className="ajuda">
                  Nenhuma marca carregada. Use "Adicionar marca" para cadastrar a primeira —
                  ou recarregue a página, se o cadastro deveria ter vindo do servidor.
                </td>
              </tr>
            ) : null}
            {rascunhos.map(rascunho => {
              /* Card sem de-para nenhum não é alimentado pelo coletor. Só na
                 base MALHAS isso é esperado (vazio = a base toda); em qualquer
                 outra é aviso — comparar com 'CNF' deixaria base nova sem ele. */
              const semDePara = rascunho.marcasErp.trim() === '' && rascunho.catalogosErp.trim() === ''
              const baseInteira = rascunho.base === 'MALHAS'
              const alertaDePara = semDePara && !baseInteira
              /* Marca do Linx só importa para quem EXIBE sell out: cobrá-la da
                 Alakazoo seria pedir cadastro de um card que não tem a linha. */
              const marcasLinxLidas = marcasLinxDoTexto(rascunho.marcasLinx)
              const alertaSellOut =
                rascunho.subitens.includes('sellout') && marcasLinxLidas.ids.length === 0
              return (
                <tr key={rascunho.chave}>
                  <td className="num">
                    <input
                      type="number" className="estreito" step={1} min={1}
                      value={rascunho.ordem}
                      onChange={evento => aoAlterar(rascunho.chave, { ordem: evento.target.value })}
                    />
                  </td>

                  <td>
                    <div className="logo-celula">
                      <div className="logo-previa">
                        {rascunho.previa
                          ? <img src={rascunho.previa} alt="" />
                          : rascunho.logo
                            ? <img src={rascunho.logo} alt="" />
                            : (rascunho.sigla || '—')}
                      </div>
                      <div>
                        <label className="botao" htmlFor={`logo-${rascunho.chave}`}>Enviar logo</label>
                        <input
                          id={`logo-${rascunho.chave}`}
                          type="file" accept=".png,.svg,.webp" hidden
                          onChange={evento => {
                            aoEscolherLogo(rascunho.chave, evento.target.files?.[0] ?? null)
                            /* Limpa o input para que escolher o MESMO arquivo de
                               novo ainda dispare o `change`. */
                            evento.target.value = ''
                          }}
                        />
                        <div className="logo-regra">PNG, SVG ou WEBP · até 512 KB</div>
                        {rascunho.arquivo
                          ? <div className="logo-regra">sobe ao salvar: {rascunho.arquivo.name}</div>
                          : null}
                      </div>
                    </div>
                  </td>

                  <td>
                    <input
                      type="text" value={rascunho.nome} placeholder="Nome no painel"
                      onChange={evento => aoAlterar(rascunho.chave, { nome: evento.target.value })}
                    />
                    <div className="logo-celula">
                      {rascunho.nova ? (
                        <>
                          <input
                            type="text" value={rascunho.id} placeholder="id (akz)"
                            onChange={evento => aoAlterar(rascunho.chave, { id: evento.target.value })}
                          />
                          <input
                            type="text" value={rascunho.sigla} placeholder="sigla"
                            onChange={evento => aoAlterar(rascunho.chave, { sigla: evento.target.value })}
                          />
                        </>
                      ) : (
                        <div className="logo-regra">
                          id {rascunho.id} · sigla {rascunho.sigla}
                          {rascunho.ativa ? '' : ' · desativada'}
                        </div>
                      )}
                    </div>
                  </td>

                  <td>
                    <select
                      value={rascunho.base}
                      onChange={evento => aoAlterar(rascunho.chave, { base: evento.target.value })}
                    >
                      {/* A base do rascunho entra sempre: com o cadastro ainda
                          não carregado a lista vem vazia, e um `select` sem
                          opção mostra o campo em branco e perde a base. */}
                      {(bases.includes(rascunho.base) ? bases : [rascunho.base, ...bases])
                        .map(base => <option key={base} value={base}>{base}</option>)}
                    </select>
                  </td>

                  <td>
                    <div className="celula-depara">
                      <input
                        type="text" value={rascunho.marcasErp}
                        placeholder="ALAKAZOO; ALAKAZOO KIDS"
                        style={alertaDePara ? { borderColor: 'var(--atencao)' } : undefined}
                        title={alertaDePara
                          ? 'Sem de-para: o coletor não sabe o que somar neste card.'
                          : 'Valores de Produto Marca, separados por ponto e vírgula.'}
                        onChange={evento => aoAlterar(rascunho.chave, { marcasErp: evento.target.value })}
                      />
                      <div className="logo-regra">
                        Produto Marca{semDePara && baseInteira ? ' · vazio = base toda' : ''}
                      </div>
                      <input
                        type="text" value={rascunho.catalogosErp}
                        placeholder="ESPECIAIS PL"
                        onChange={evento => aoAlterar(rascunho.chave, { catalogosErp: evento.target.value })}
                      />
                      <div className="logo-regra">Catálogo · hoje só a Private Label</div>
                      <input
                        type="text" value={rascunho.marcasLinx}
                        placeholder="1; 3"
                        style={alertaSellOut ? { borderColor: 'var(--atencao)' } : undefined}
                        title={alertaSellOut
                          ? 'Este card mostra Sell out e não tem marca do Linx: o valor fica vazio.'
                          : 'Números de id_brand do dw.brand do Linx, separados por ponto e vírgula.'}
                        onChange={evento => aoAlterar(rascunho.chave, { marcasLinx: evento.target.value })}
                      />
                      <div className="logo-regra">
                        {/* O nome ao lado do número é o que impede cadastrar 2
                            (Lunender) acreditando ser 3 (Hangar 33). */}
                        {marcasLinxLidas.ids.length > 0
                          ? `Linx · ${nomesDasMarcasLinx(marcasLinxLidas.ids)}`
                          : 'Marca do Linx (sell out) · 1 Lez · 3 H33'}
                      </div>
                    </div>
                  </td>

                  <td>
                    <div className="marcadores">
                      {subitensDisponiveis.map(subitem => (
                        <label className="marcador" key={subitem.codigo}>
                          <input
                            type="checkbox"
                            checked={rascunho.subitens.includes(subitem.codigo)}
                            onChange={() => aoAlternarSubitem(rascunho.chave, subitem.codigo)}
                          />
                          {' '}{subitem.rotulo}
                        </label>
                      ))}
                    </div>
                  </td>

                  <td>
                    {/* Não existe rota de remoção: a divisão guarda meta, logo e
                        histórico. Desativar é o que tira a marca do telão — e é
                        reversível no meio do evento. */}
                    {rascunho.nova ? (
                      <button className="botao" title="Descartar linha"
                        onClick={() => aoDescartar(rascunho.chave)}>✕</button>
                    ) : (
                      <button className="botao"
                        title={rascunho.ativa
                          ? 'Tira a marca do telão; a meta e o histórico ficam.'
                          : 'Devolve a marca ao telão.'}
                        onClick={() => aoAlterar(rascunho.chave, { ativa: !rascunho.ativa })}>
                        {rascunho.ativa ? 'Desativar' : 'Reativar'}
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        <div className="rodape-acoes">
          <button className="botao primario" disabled={salvando} onClick={aoSalvar}>
            {salvando ? 'Salvando…' : 'Salvar marcas'}
          </button>
          <button className="botao" disabled={salvando} onClick={aoAdicionar}>Adicionar marca</button>
          <span className={salvo ? 'salvo ver' : 'salvo'}>Marcas salvas.</span>
          {erro ? <span className="erro">{erro}</span> : null}
        </div>
      </div>
    </section>
  )
}
